/* global Office */
import { getEmailAndTemplate } from './getPopulatedTemplate'
import { getSnippetBody, updateCounts, toggleSnippetInsertOverlay } from '../redux/snippets'
import makePromise from '../office/make-promise'
import { replaceAttachmentUrls } from '../services/attachments'
import { notify } from '../redux/notifications'
import { messages } from './messages'
import { removeEmailFooter, addTemplateWrapper } from './utils'
import { addBreadcrumb, captureException, withScope } from '../sentry-config'

export const toggleTemplateDialog = isWarningDialogShow => ({
  type: 'TOGGLE_DIALOG',
  isWarningDialogShow,
})

// eslint-disable-next-line max-len
export const toggleTemplateInsertOverlay = isOverlayVisible => dispatch =>
  new Promise(resolve => {
    resolve(
      dispatch({
        type: 'TOGGLE_OVERLAY',
        isOverlayVisible,
      })
    )
  })

export const getOutlookCustomData = () => {
  addBreadcrumb({ message: 'Getting outlook custom data', category: 'office-js' })
  return new Promise(resolve => {
    const item = Office.context.mailbox.item
    return item.loadCustomPropertiesAsync(results => resolve(results.value))
  })
}

export const insertOutlookCustomData = (key, value) => {
  addBreadcrumb({ message: 'Inserting outlook custom data', category: 'office-js' })
  return new Promise(resolve => {
    getOutlookCustomData()
      .then(customProps => {
        if (customProps) {
          if (value !== null) {
            customProps.set(key, value)
          } else {
            customProps.remove(key)
          }
          return customProps.saveAsync(result =>
            resolve({ type: 'INSERT_OUTLOOK_CUSTOM_DATA', value: result.value })
          )
        }
      })
      .catch(captureException)
  })
}
export const insertOutlookAttachments = attachments => () => {
  addBreadcrumb({ message: 'Inserting outlook attachments', category: 'office-js' })
  const item = Office.context.mailbox.item
  const addFile = makePromise(item.addFileAttachmentAsync, item)
  return Promise.all(attachments.map(a => addFile(a.url, a.attachment_file_name, {})))
    .then(results => insertOutlookCustomData('attachments', results))
    .catch(captureException)
}

export const removeOutlookAttachments = () => () => {
  const item = Office.context.mailbox.item
  addBreadcrumb({ message: 'Removing outlook attachments', category: 'office-js' })
  const removeFile = makePromise(item.removeAttachmentAsync, item)
  return getOutlookCustomData()
    .then(results => {
      if (results.get('attachments')) {
        Promise.all(results.get('attachments').map(a => removeFile(a, {})))
      }
      return true
    })
    .catch(captureException)
}

export const insertInNewReply = (body, attachments) => {
  addBreadcrumb({ message: 'Inserting template in new reply', category: 'office-js' })
  const item = Office.context.mailbox.item
  return new Promise(resolve => {
    item.displayReplyForm({
      htmlBody: body,
      attachments: attachments.map(a => ({
        type: Office.MailboxEnums.AttachmentType.File,
        name: a.attachment_file_name,
        url: a.url,
      })),
      callback: result => {
        if (result.error) {
          const errorToCapture =
            result.error instanceof Error
              ? result.error
              : new Error(result.error.message || 'Unknown error')

          withScope(scope => {
            scope.setExtra('error_data', {
              code: result.error?.code || 'N/A',
              name: result.error?.name || 'No name',
              stack: result.error?.stack || 'No stack',
              rawError: result.error,
            })

            captureException(errorToCapture)
          })
        }
        resolve(result)
      },
    })
  })
}

// eslint-disable-next-line max-len
export const insertTemplateToEmail = templateId => (dispatch, getState, { office }) =>
  dispatch(toggleTemplateInsertOverlay(true))
    .then(() => dispatch(getEmailAndTemplate(templateId)))
    .then(() => dispatch(removeOutlookAttachments()))
    .then(() => {
      addBreadcrumb({
        message: 'Getting body, subject and attachments from state',
        category: 'state',
      })
      const { body: templateBody, subject, attachments: templateAttachments } =
        getState().templates.template || {}
      const body = addTemplateWrapper(removeEmailFooter(templateBody))

      const attachments = replaceAttachmentUrls(templateAttachments, getState().auth.accessToken)
      if (Office.context.mailbox.item.internetMessageId) {
        return dispatch(insertInNewReply(body, attachments))
      } else {
        addBreadcrumb({ message: 'Inserting template', category: 'office-js' })
        return office.item
          .setBody(body)
          .then(() => dispatch(insertOutlookAttachments(attachments)))
          .then(() => {
            addBreadcrumb({ message: 'Inserting subject', category: 'office-js' })
            office.item.setSubjectUnlessPresent(subject)
          })
          .then(() => dispatch(insertOutlookCustomData('templateId', templateId)))
          .catch(e => {
            captureException(e)
            dispatch(
              notify({
                type: 'error',
                message: messages.textNotifyTemplateNotInserted,
              })
            )
          })
      }
    })
    .then(() => dispatch(toggleTemplateInsertOverlay(false)))
    .then(() => dispatch(toggleTemplateDialog(true)))
    .catch(e => {
      captureException(e)
      dispatch(toggleTemplateInsertOverlay(false))
    })

export const insertSnippetToEmail = snippetId => (dispatch, getState, { office }) => {
  dispatch(toggleSnippetInsertOverlay(true))
  dispatch(getSnippetBody(snippetId))
    .then(body => office.item.insertToBody(removeEmailFooter(body)))
    .then(updateCounts(snippetId))
    .then(() => dispatch(toggleSnippetInsertOverlay(false)))
    .catch(e => {
      captureException(e)
      dispatch(toggleSnippetInsertOverlay(false))
    })
}

export const insertCrmReferences = references => dispatch =>
  dispatch(insertOutlookCustomData('crmReferences', references))

export const insertTimeSlotsToEmail = body => (dispatch, getState, { office }) => {
  office.item.insertToBody(body)
}
